import { useEffect, useMemo, useRef } from 'react';

import type { OrderDetailsDialogHandle } from 'pages/orders/order_history/components/OrderDetailsDialog';
import { OrderDetailsDialog } from 'pages/orders/order_history/components/OrderDetailsDialog';
import { OrderHistoryTable } from 'pages/orders/order_history/components/OrderHistoryTable';
import { SearchFilters } from 'pages/orders/order_history/components/SearchFilters';
import { OrderHistoryContext } from 'pages/orders/order_history/context';
import { DefaultLayout, PageWithHeader } from 'components/layout';
import { Dialog, DialogHandle } from 'components/modal_manager';
import GMetrixToast from 'components/GMetrixToast/GMetrixToast';
import { helpContent } from 'pages/orders/order_history/help';
import { Country, CountryApi, State } from 'apis/CountryApi';
import { Order, OrderApi, OrderStatus } from 'apis/OrderApi';
import { getIdFromURL, getStringFromURL } from 'utils/other';
import { PartnerApi, PartnerTypes } from 'apis/PartnerApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, Card } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

export type LicenseOption = {
    Id: number;
    Name: string;
};

export type OrderHistoryFilters = {
    country: number;
    customerId: string;
    endDate: string;
    license: number;
    orderedBy: string;
    orderId: string;
    purchaseOrder: string;
    startDate: string;
    state: number;
};

const defaultFilters: OrderHistoryFilters = {
    country: 0,
    customerId: '',
    endDate: '',
    license: 0,
    orderedBy: '',
    orderId: '',
    purchaseOrder: '',
    startDate: '',
    state: 0
};

export type OrderHistoryContextValue = {
    clearFilters: () => void;
    countries: Country[];
    error: string;
    fetchOrders: (status?: OrderStatus, received?: boolean) => Promise<void>
    filteredOrders: Order[];
    filters: OrderHistoryFilters;
    handleCancelOrderConfirmation: (id: number) => void;
    handleCountryChange: (countryId: number) => void;
    handleDirectionChange: (isReceived: boolean) => Promise<void>;
    handleStatusChange: (status: OrderStatus) => Promise<void>;
    isReceived: boolean;
    isTestingCenter: boolean;
    launchDetailDialog: (order: Order) => void;
    licenses: LicenseOption[];
    loading: boolean;
    orders: Order[];
    orderStatus: OrderStatus;
    selectedPartnerName: string;
    setFilters: (partial: Partial<OrderHistoryFilters>) => void;
    states: State[];
};

export const OrderHistory = () => {

    const orderDirection = getIdFromURL('dir')?.[0] ?? 1;
    const orderType = getStringFromURL('ordType') ?? 'Processed';

    const { highestRole, selectedPartner } = useApp();
    const isTestingCenter = selectedPartner.type === PartnerTypes.TestingCenter;

    const initialReceived = isTestingCenter
        ? false
        : highestRole.id === 1
            ? true
            : orderDirection === 2;

    const initialStatus = orderType === 'Pending'
        ? OrderStatus.Pending
        : (orderType === 'Cancelled' || orderType === 'Canceled')
            ? OrderStatus.Canceled
            : OrderStatus.Processed;

    const [state, setState] = useSuperState({
        countries: [] as Country[],
        error: null as string,
        filters: defaultFilters,
        isReceived: initialReceived,
        licenses: [] as LicenseOption[],
        loading: true,
        orders: [] as Order[],
        orderStatus: initialStatus,
        states: [] as State[]
    });
    const { error } = state;

    const { formatMessage } = useIntl();

    const orderDetailsDialogRef = useRef<OrderDetailsDialogHandle>(null);
    const dialogRef = useRef<DialogHandle>(null);
    const toastRef = useRef(null);

    const clearError = () => setState({ error: null });

    const fetchOrders = async (status: OrderStatus = state.orderStatus, received: boolean = state.isReceived) => {

        try {

            setState({
                error: null,
                loading: true,
                orders: []
            });

            const orders = await OrderApi.GetAll(selectedPartner.id, status, received);

            const licenseMap = new Map<number, string>();
            orders.forEach(({ License }) => {

                if (License?.ID && License?.Name && !licenseMap.has(License.ID)) {

                    licenseMap.set(License.ID, License.Name);

                }

            });
            const licenses: LicenseOption[] = Array.from(licenseMap, ([Id, Name]) => ({ Id, Name }))
                .sort((a, b) => a.Name.localeCompare(b.Name));

            setState({
                licenses,
                loading: false,
                orders
            });

        } catch (e: any) {

            setState({
                error: e?.Message || e?.message || formatMessage({ id: 'failed_to_load_orders.bc72e0a6', defaultMessage: 'Failed to load orders.' }),
                loading: false
            });

        }

    };

    const fetchCountries = async () => {

        try {

            const countries = state.isReceived
                ? await PartnerApi.GetOrderHistoryCountries(selectedPartner.id)
                : [];

            setState({ countries });

        } catch (e: any) {

            setState({ error: e?.Message || e?.message || formatMessage({ id: 'failed_to_load_countries.6efc8543', defaultMessage: 'Failed to load countries.' }) });

        }

    };

    const handleStatusChange = async (status: OrderStatus) => {

        setState({ orderStatus: status });
        await fetchOrders(status, state.isReceived);

    };

    const handleDirectionChange = async (isReceived: boolean) => {

        setState({
            countries: [],
            filters: { ...state.filters, country: 0, customerId: '', state: 0 },
            isReceived,
            states: []
        });

        await fetchOrders(state.orderStatus, isReceived);

    };

    const handleCountryChange = (countryId: number) => setState({
        filters: { ...state.filters, country: countryId, state: 0 },
        states: []
    });

    const fetchStates = async () => {

        if (state.filters.country <= 0) return;

        try {

            const states = await CountryApi.GetStates(state.filters.country);
            setState({ states: states ?? [] });

        } catch (e: any) {

            setState({ error: e?.Message || e?.message || formatMessage({ id: 'failed_to_load_states.ec2a3c83', defaultMessage: 'Failed to load states.' }) });

        }

    };

    const clearFilters = () => setState({ filters: defaultFilters, states: [] });

    const launchDetailDialog = (order: Order) => {

        const dialog = orderDetailsDialogRef?.current;
        if (!dialog) return;

        dialog.show({ order });

    };

    const handleCancelOrder = async (id: number) => {

        try {

            const result = await OrderApi.CancelOrder(id);

            const toast = toastRef?.current;
            if (!toast) return;

            if (result.Done) {

                fetchOrders();
                toast.createToast(result.Message || formatMessage({ id: 'order_cancelled_successfully.92cb1e55', defaultMessage: 'Order cancelled successfully.' }), 'success');

            } else {

                toast.createToast(result.Message || formatMessage({ id: 'failed_to_cancel_order.b46cefc3', defaultMessage: 'Failed to cancel order.' }), 'danger');

            }

        } catch (e: any) {

            setState({ error: e?.Message || e?.message || formatMessage({ id: 'failed_to_cancel_order.b46cefc3', defaultMessage: 'Failed to cancel order.' }) });

        }

    };

    const handleCancelOrderConfirmation = (id: number) => {

        const dialog = dialogRef?.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.OKAction({
                    label: formatMessage({ id: 'cancel_order.3d3a9ad5', defaultMessage: 'Cancel Order', description: 'Titulo' }),
                    onClick: () => handleCancelOrder(id),
                    variant: 'danger'
                })
            ],
            body: (
                <p>
                    {<FormattedMessage defaultMessage="Are you sure you want to cancel this order?" id="are_you_sure_you_want_to_cancel_this_ord.e57c3af6" />}
                </p>
            ),
            title: formatMessage({ id: 'confirm_cancel_order.83d6c925', defaultMessage: 'Confirm Cancel Order' })
        });

    };

    // Apply client-side filters
    const filteredOrders = useMemo(() => {

        const { filters } = state;

        // Pre-lowercase string filters to avoid repeated calls inside the loop
        const poFilter = filters.purchaseOrder.toLowerCase();
        const idFilter = filters.orderId.toLowerCase();
        const byFilter = filters.orderedBy.toLowerCase();
        const custFilter = filters.customerId.toLowerCase();

        // Clamp date filters to start/end of day for inclusive range matching
        const startDateValue = filters.startDate ? new Date(new Date(filters.startDate).setHours(0, 0, 0, 0)) : null;
        const endDateValue = filters.endDate ? new Date(new Date(filters.endDate).setHours(23, 59, 59, 999)) : null;

        // Filter: text (skip if empty), date range, license, and received-only partner fields
        return [...state.orders]
            .filter(order => (!poFilter || order.PurchaseOrder?.toLowerCase().includes(poFilter)) &&
                (!idFilter || order.OrderControl?.toLowerCase().includes(idFilter)) &&
                (!byFilter || order.Partner?.Name?.toLowerCase().includes(byFilter)) &&
                (!startDateValue || new Date(order.DateCreate) >= startDateValue) &&
                (!endDateValue || new Date(order.DateCreate) <= endDateValue) &&
                (!filters.license || order.License?.ID === filters.license) &&
                (!state.isReceived || !filters.country || order.Partner?.OperatingCountries?.some(c => c.Id === filters.country)) &&
                (!state.isReceived || !filters.state || order.Partner?.StateID === filters.state) &&
                (!state.isReceived || !custFilter || order.Partner?.CustomerID?.some(c => c.ID?.toLowerCase().includes(custFilter)))
            )
            .sort((a, b) => new Date(b.DateCreate).getTime() - new Date(a.DateCreate).getTime()); // Most recent orders first

    }, [state.filters, state.isReceived, state.orders]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {

        fetchOrders();

    }, []);

    // Whenever we switch between placed/received, update the countries list
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {

        fetchCountries();

    }, [state.isReceived]);

    // Whenever the country selection changes, update the states list
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {

        fetchStates();

    }, [state.filters.country]);

    const context: OrderHistoryContextValue = {
        clearFilters,
        countries: state.countries,
        error: state.error,
        fetchOrders,
        filteredOrders,
        filters: state.filters,
        handleCancelOrderConfirmation,
        handleCountryChange,
        handleDirectionChange,
        handleStatusChange,
        isReceived: state.isReceived,
        isTestingCenter,
        launchDetailDialog,
        licenses: state.licenses,
        loading: state.loading,
        orders: state.orders,
        orderStatus: state.orderStatus,
        selectedPartnerName: selectedPartner.name,
        setFilters: (partial: Partial<OrderHistoryFilters>) => setState({ filters: { ...state.filters, ...partial } }),
        states: state.states
    };

    return (
        <DefaultLayout pageTitle={formatMessage({ id: 'order_history.8a7d4817', defaultMessage: 'Order History' })}>
            <OrderHistoryContext.Provider value={context}>

                <div className="order-history d-flex flex-column gap-3">
                    <PageWithHeader
                        headerConfig={{
                            actions: [
                                {
                                    label: formatMessage({ id: 'view_my_order_history.47665ace', defaultMessage: 'View My Order History' }),
                                    onClick: async () => {

                                        await PartnerApi.ClearSelectedPartner();
                                        window.location.href = `/orders/history?dir=${highestRole.id === 1 ? 0 : 1}&ordType=Processed`;

                                    }
                                }
                            ],
                            cta: {
                                label: formatMessage({ id: 'view_customer_order_history.04c9f1be', defaultMessage: 'View Customer Order History' }),
                                to: `/partners/lookup?RefPage=/orders/history?dir=${state.isReceived ? 2 : 1}%26ordType=${OrderStatus[state.orderStatus]}`
                            },
                            helpContent: helpContent,
                            title: formatMessage({ id: 'order_history.8a7d4817', defaultMessage: 'Order History' })
                        }}
                    >

                        <GMetrixToast ref={toastRef} />

                        <Card body>

                            <Alert dismissible onClose={clearError} show={!!error} variant="danger">
                                {error}
                            </Alert>

                            <SearchFilters />

                            <OrderHistoryTable />

                        </Card>

                        <OrderDetailsDialog ref={orderDetailsDialogRef} />

                        <Dialog ref={dialogRef} />
                    </PageWithHeader>
                </div>

            </OrderHistoryContext.Provider>
        </DefaultLayout>
    );

};
